export type RawCategoryEntry = {
  key: string
  title: string
  payload?: CategoryPayload
  label?: string
  parentKey?: string
  defaultChildKey?: string
}

export type RawCategoryGroup = {
  sectorKey: string
  entries: RawCategoryEntry[]
}

export type RawStandaloneCategory = RawCategoryEntry & { sectorKey: string }

export const categoryGroups = {
  aggregatedPlus: 'Agregado +',
  aggregatedManagement: 'Agregado Gestão Empresarial',
  aggregatedMinus: 'Agregado -',
  toolsPlus: 'Ferramentas +',
  toolsMinus: 'Ferramentas -',
  premium: 'Premium',
  others: 'Outros',
} as const

export type CategoryGroup = (typeof categoryGroups)[keyof typeof categoryGroups]

export type CategoryPayload = Record<string, string | number | boolean | string[]> & {
  categoryGroup?: CategoryGroup
}

export const solutionPerformanceCategoryTitle = 'Desempenho das soluções'
export const solutionAttendancesAndPerformanceVariableKey = 'solutionAttendancesAndPerformance'
export const solutionAttendancesAndPerformanceVariableTitle =
  'Quantidade de atendimentos e desempenho'
export const solutionPerformanceVariableKey = 'solutionPerformance'
export const solutionPerformanceVariableTitle = 'Desempenho das soluções'

export const monitorProductFamilyCodes = {
  sebraeIngresse: [
    '372000085884',
    '372000085885',
    '372000085891',
    '372000063511',
    '372000030641',
    '372000063517',
    'S26NA3CS15070',
  ],
  familiaEmpretec: ['24&26', '372000043604', '372000063240', '372000090106'],
} as const

export const monitorPortfolioNames = [
  'Gestão Empresarial',
  'Educação Empreendedora',
  'Cidade Empreendedora',
  'Territórios Empreendedores',
  'Inovação',
  'Sustentabilidade',
  'Agronegócios',
  'Comércio e Serviços',
  'Energias Renováveis',
  'Indústria',
  'Turismo de Experiência',
  'Economia Criativa',
  'Liderança',
]

type PortfolioKpiDefinition = {
  title: string
  kpiId?: string
  idsId?: string
  portfolio?: string
  attendancesPerProduct?: boolean
  solutionPerformance?: boolean
  customKey?: string
}

const makeDefaultPortfolioKpis = (param: {
  attendanceKpiId?: string
  percentTotalKpiId?: string
  percentPresentialKpiId?: string
  percentDistanceKpiId?: string
  portfolio?: string
}): PortfolioKpiDefinition[] => {
  const kpis: PortfolioKpiDefinition[] = []

  if (param.attendanceKpiId) {
    kpis.push({
      kpiId: param.attendanceKpiId,
      title: 'Atendimentos',
    })
  }

  kpis.push({
    kpiId: '1701',
    title: solutionPerformanceCategoryTitle,
    portfolio: param.portfolio,
    attendancesPerProduct: true,
    solutionPerformance: true,
  })

  kpis.push({
    kpiId: '1801',
    title: 'Turmas por produto',
    portfolio: param.portfolio,
    attendancesPerProduct: true,
  })

  if (param.percentTotalKpiId) {
    kpis.push({
      kpiId: param.percentTotalKpiId,
      title: 'Percentual de Utilização (Total)',
    })
  }

  if (param.percentPresentialKpiId) {
    kpis.push({
      kpiId: param.percentPresentialKpiId,
      title: 'Percentual de Utilização (Presencial)',
    })
  }

  if (param.percentDistanceKpiId) {
    kpis.push({
      kpiId: param.percentDistanceKpiId,
      title: 'Percentual de Utilização (À distância)',
    })
  }

  return kpis
}

// Portfolios customizados
const gestaoEmpresarialKpis: PortfolioKpiDefinition[] = [
  { kpiId: '0501', title: 'Atendimentos' },
  {
    kpiId: '1701',
    portfolio: 'Gestão Empresarial',
    title: solutionPerformanceCategoryTitle,
    attendancesPerProduct: true,
    solutionPerformance: true,
  },
  {
    kpiId: '1801',
    portfolio: 'Gestão Empresarial',
    title: 'Turmas por produto',
    attendancesPerProduct: true,
  },
  { kpiId: '0601', title: 'Indicador Cliente 360 (Total)' },
  { kpiId: '0602', title: 'Indicador Cliente 360 (Presencial)' },
  { kpiId: '0603', title: 'Indicador Cliente 360 (À distância)' },
  { kpiId: '1001', title: 'Percentual de utilização (Total)' },
  { kpiId: '1002', title: 'Percentual de utilização (Presencial)' },
  { kpiId: '1003', title: 'Percentual de utilização (À distância)' },
  {
    title: 'Limite mínimo de participantes na turma',
    customKey: 'monitorPortfolioCourseClassesMetrics',
  },
]

const educacaoEmpreendedoraKpis: PortfolioKpiDefinition[] = [
  { kpiId: '0502', title: 'Atendimentos' },
  {
    kpiId: '1701',
    portfolio: 'Educação Empreendedora',
    title: solutionPerformanceCategoryTitle,
    attendancesPerProduct: true,
    solutionPerformance: true,
  },
  {
    kpiId: '1801',
    portfolio: 'Educação Empreendedora',
    title: 'Turmas por produto',
    attendancesPerProduct: true,
  },
  { kpiId: '0604', title: 'Indicador Educação que Transforma (Total)' },
  { kpiId: '0605', title: 'Indicador Educação que Transforma (Presencial)' },
  { kpiId: '0606', title: 'Indicador Educação que Transforma (À distância)' },
  { kpiId: '1004', title: 'Percentual de Utilização (Total)' },
  { kpiId: '1005', title: 'Percentual de Utilização (Presencial)' },
  { kpiId: '1006', title: 'Percentual de Utilização (À distância)' },
]

// Portfólios genéricos
const cidadeEmpreendedoraKpis = makeDefaultPortfolioKpis({
  attendanceKpiId: '0503',
  percentTotalKpiId: '1007',
  percentPresentialKpiId: '1008',
  percentDistanceKpiId: '1009',
  portfolio: 'Cidade Empreendedora',
})

const territoriosEmpreendedoresKpis = makeDefaultPortfolioKpis({
  attendanceKpiId: '0504',
  percentTotalKpiId: '1010',
  percentPresentialKpiId: '1011',
  percentDistanceKpiId: '1012',
  portfolio: 'Territórios Empreendedores',
})

const inovacaoKpis = makeDefaultPortfolioKpis({
  attendanceKpiId: '0505',
  percentTotalKpiId: '1013',
  percentPresentialKpiId: '1014',
  percentDistanceKpiId: '1015',
  portfolio: 'Inovação',
})

const sustentabilidadeKpis = makeDefaultPortfolioKpis({
  attendanceKpiId: '0506',
  percentTotalKpiId: '1016',
  percentPresentialKpiId: '1017',
  percentDistanceKpiId: '1018',
  portfolio: 'Sustentabilidade',
})

const agronegociosKpis = makeDefaultPortfolioKpis({
  attendanceKpiId: '0507',
  percentTotalKpiId: '1019',
  percentPresentialKpiId: '1020',
  percentDistanceKpiId: '1021',
  portfolio: 'Agronegócios',
})

const comercioServicoKpis = makeDefaultPortfolioKpis({
  attendanceKpiId: '0508',
  percentTotalKpiId: '1022',
  percentPresentialKpiId: '1023',
  percentDistanceKpiId: '1024',
  portfolio: 'Comércio e Serviços',
})

const energiasRenovaveisKpis = makeDefaultPortfolioKpis({
  attendanceKpiId: '0509',
  percentTotalKpiId: '1025',
  percentPresentialKpiId: '1026',
  percentDistanceKpiId: '1027',
  portfolio: 'Energias Renováveis',
})

const industriaKpis = makeDefaultPortfolioKpis({
  attendanceKpiId: '0510',
  percentTotalKpiId: '1028',
  percentPresentialKpiId: '1029',
  percentDistanceKpiId: '1030',
  portfolio: 'Indústria',
})

const turismoDeExperienciaKpis = makeDefaultPortfolioKpis({
  attendanceKpiId: '0512',
  percentTotalKpiId: '1031',
  percentPresentialKpiId: '1032',
  // não existe percentual à distância
  portfolio: 'Turismo de Experiência',
})

const economiaCriativaKpis = makeDefaultPortfolioKpis({
  attendanceKpiId: '0513',
  percentTotalKpiId: '1034',
  // não existe percentual presencial nesse portfólio
  percentDistanceKpiId: '1036',
  portfolio: 'Economia Criativa',
})

const liderancaKpis = makeDefaultPortfolioKpis({
  attendanceKpiId: '0514',
  percentTotalKpiId: '1037',
  percentPresentialKpiId: '1038',
  percentDistanceKpiId: '1039',
  portfolio: 'Liderança',
})

const portfolioKpiOverrides: Record<string, PortfolioKpiDefinition[]> = {
  'Gestão Empresarial': gestaoEmpresarialKpis,
  'Educação Empreendedora': educacaoEmpreendedoraKpis,
  'Cidade Empreendedora': cidadeEmpreendedoraKpis,
  'Territórios Empreendedores': territoriosEmpreendedoresKpis,
  Inovação: inovacaoKpis,
  Sustentabilidade: sustentabilidadeKpis,
  Agronegócios: agronegociosKpis,
  'Comércio e Serviços': comercioServicoKpis,
  'Energias Renováveis': energiasRenovaveisKpis,
  Indústria: industriaKpis,
  'Turismo de Experiência': turismoDeExperienciaKpis,
  'Economia Criativa': economiaCriativaKpis,
  Liderança: liderancaKpis,
}

export const slugify = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]/g, '')

const portfolioKey = (portfolio: string) => `monitorPortfolio${slugify(portfolio)}`

const normalizeCategoryGroupTitle = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()

const inferCategoryGroupForPortfolio = (portfolio: string): CategoryGroup =>
  portfolio === 'Gestão Empresarial'
    ? categoryGroups.aggregatedManagement
    : categoryGroups.aggregatedMinus

const inferCategoryGroupForPortfolioKpi = (portfolio: string, kpiTitle: string): CategoryGroup => {
  if (kpiTitle === 'Atendimentos por produto') return categoryGroups.others
  if (kpiTitle === 'Turmas por produto') return categoryGroups.others
  if (kpiTitle === solutionPerformanceCategoryTitle) return categoryGroups.others
  if (kpiTitle === 'Limite mínimo de participantes na turma') return categoryGroups.others

  const normalizedTitle = normalizeCategoryGroupTitle(kpiTitle)

  if (normalizedTitle.includes('indice de desempenho de solucoes')) return categoryGroups.others

  const isPercentualDeUtilizacao = normalizedTitle.startsWith('percentual de utilizacao')

  if (portfolio === 'Gestão Empresarial') {
    if (
      kpiTitle === 'Atendimentos' ||
      normalizedTitle.startsWith('indicador cliente 360') ||
      isPercentualDeUtilizacao
    ) {
      return categoryGroups.aggregatedManagement
    }
  } else {
    if (
      kpiTitle === 'Atendimentos' ||
      normalizedTitle.startsWith('indicador educacao que transforma') ||
      isPercentualDeUtilizacao
    ) {
      return categoryGroups.aggregatedMinus
    }
  }

  return categoryGroups.others
}

const createPortfolioEntries = (portfolio: string): RawCategoryEntry[] => {
  const entries: RawCategoryEntry[] = []

  const kpis =
    portfolioKpiOverrides[portfolio] ??
    makeDefaultPortfolioKpis({
      portfolio,
    })

  const parentKey = portfolioKey(portfolio)

  // Define a primeira entrada como defaultChildKey do portfólio, adaptado para inclusão de customKey
  const firstEntryKey = kpis.length
    ? (kpis[0].customKey ?? `${parentKey}${slugify(kpis[0].title)}`)
    : undefined

  entries.push({
    key: parentKey,
    title: portfolio,
    payload: { categoryGroup: inferCategoryGroupForPortfolio(portfolio) },
    ...(firstEntryKey ? { defaultChildKey: firstEntryKey } : {}),
  })

  kpis.forEach(kpi => {
    const payload: CategoryPayload = {
      categoryGroup: inferCategoryGroupForPortfolioKpi(portfolio, kpi.title),
    }

    if (kpi.kpiId) {
      payload.kpiId = kpi.kpiId
    }

    if (kpi.idsId) {
      payload.idsId = kpi.idsId
    }

    if (kpi.portfolio) {
      payload.portfolio = kpi.portfolio
    }

    if (kpi.attendancesPerProduct) {
      payload.attendancesPerProduct = true
    }

    if (kpi.solutionPerformance) {
      payload.solutionPerformance = true
    }

    const entryKey = kpi.customKey ?? `${parentKey}${slugify(kpi.title)}`

    entries.push({
      key: entryKey,
      title: kpi.title,
      parentKey,
      label: portfolio,
      payload,
    })
  })

  return entries
}

const monitorPortfolioEntries = monitorPortfolioNames.flatMap(createPortfolioEntries)
const gestaoEmpresarialSolutionPerformanceCategoryKey = `${portfolioKey('Gestão Empresarial')}${slugify(
  solutionPerformanceCategoryTitle,
)}`

export const groupedCategoryData: RawCategoryGroup[] = [
  {
    sectorKey: 'monitorServiceOverview',
    entries: [
      {
        key: 'monitorServiceOverview',
        parentKey: 'monitorServiceOverviewDropdown',
        payload: { kpiId: '0101', categoryGroup: categoryGroups.aggregatedPlus },
        title: 'Atendimentos SEBRAE',
        label: 'Número total de atendimentos',
      },
      {
        key: 'monitorServiceOverviewHasProduct',
        parentKey: 'monitorServiceOverviewDropdown',
        payload: { kpiId: '0201', categoryGroup: categoryGroups.aggregatedMinus },
        title: 'Atendimentos SEBRAE',
        label: 'Número total de atendimentos com vínculo a produto',
      },
      {
        key: 'monitorServiceOverviewCoursesAndWorkshops',
        parentKey: 'monitorServiceOverviewDropdown',
        payload: { kpiId: '0301', categoryGroup: categoryGroups.aggregatedMinus },
        title: 'Atendimentos SEBRAE',
        label: 'Número total de atendimentos de cursos e oficinas',
      },
      {
        key: 'monitorServiceOverviewPortfolio',
        parentKey: 'monitorServiceOverviewDropdown',
        payload: { kpiId: '0401', categoryGroup: categoryGroups.aggregatedPlus },
        title: 'Atendimentos nos portfólios',
        label: 'Número total de atendimentos nos portfólios',
      },
    ],
  },
  {
    sectorKey: 'monitorOnlineCourses',
    entries: [
      {
        key: 'monitorAttendanceEad',
        parentKey: 'monitorOnlineCoursesDropdown',
        title: 'Cursos Online',
        label: 'Atendimentos EAD',
        payload: { kpiId: '1301', categoryGroup: categoryGroups.aggregatedMinus },
      },
      {
        key: 'monitorAttendanceChatbot',
        parentKey: 'monitorOnlineCoursesDropdown',
        title: 'Cursos Online',
        label: 'Atendimentos Chatbot',
        payload: { kpiId: '1501', categoryGroup: categoryGroups.aggregatedMinus },
      },
      // { key: 'monitorEnrollmentEad', title: 'Matrículas EAD', kpiId: '1401' }, // ocultado propositalmente
      {
        key: 'eadEnrollmentDropdown',
        parentKey: 'monitorOnlineCoursesDropdown',
        title: 'Matrículas no EAD',
        defaultChildKey: 'eadEnrollment',
        payload: { categoryGroup: categoryGroups.others },
      },
      {
        key: 'eadEnrollment',
        title: 'Geral',
        parentKey: 'eadEnrollmentDropdown',
        payload: { categoryGroup: categoryGroups.aggregatedMinus },
      },
      {
        key: 'eadEnrollmentCourses',
        title: 'Todas as matrículas do portfólio',
        parentKey: 'eadEnrollmentDropdown',
        payload: { categoryGroup: categoryGroups.others },
      },
      {
        key: 'eadEnrollmentCoursesOutsidePortfolio',
        title: 'Matrículas fora do portfólio',
        parentKey: 'eadEnrollmentDropdown',
        payload: { categoryGroup: categoryGroups.others },
      },
      {
        key: 'eadAttendanceDropdown',
        parentKey: 'monitorOnlineCoursesDropdown',
        title: 'Empresas atendidas pelo EAD',
        defaultChildKey: 'eadAttendance',
        payload: { categoryGroup: categoryGroups.others },
      },
      {
        key: 'eadAttendance',
        title: 'Geral',
        parentKey: 'eadAttendanceDropdown',
        payload: { categoryGroup: categoryGroups.aggregatedMinus },
      },
      {
        key: 'eadAttendanceSegment',
        title: 'Por segmentos',
        parentKey: 'eadAttendanceDropdown',
        payload: { categoryGroup: categoryGroups.others },
      },
      {
        key: 'eadAttendanceCnaeDivision',
        title: 'Por divisão CNAE',
        parentKey: 'eadAttendanceDropdown',
        payload: { categoryGroup: categoryGroups.others },
      },
      {
        key: 'eadAttendanceSize',
        title: 'Por portes',
        parentKey: 'eadAttendanceDropdown',
        payload: { categoryGroup: categoryGroups.others },
      },
      {
        key: 'eadAttendanceMaturity',
        title: 'Por maturidades',
        parentKey: 'eadAttendanceDropdown',
        payload: { categoryGroup: categoryGroups.others },
      },
    ],
  },
  {
    sectorKey: 'monitorAppSebrae',
    entries: [
      {
        key: 'monitorAtendimentosAppSebrae',
        parentKey: 'monitorAppSebraeDropdown',
        title: 'App SEBRAE',
        label: 'Atendimentos App SEBRAE',
        payload: { kpiId: '5901', categoryGroup: categoryGroups.toolsPlus },
      },
      {
        key: 'monitorServicosFinanceirosAppSebrae',
        parentKey: 'monitorAppSebraeDropdown',
        title: 'App SEBRAE',
        label: 'Serviços financeiros',
        payload: { kpiId: '6101', categoryGroup: categoryGroups.toolsMinus },
      },
      // Dados até 2022, motivo por estar oculto
      // {
      //   key: 'monitorcAcessoAplicativoAppSebrae',
      //   title: 'App SEBRAE',
      //   label: 'Acesso ao aplicativo',
      //   payload: { kpiId: '6102' },
      // },
      {
        key: 'monitorAtendimentoViaAssistenteDigitalAppSebrae',
        parentKey: 'monitorAppSebraeDropdown',
        title: 'App SEBRAE',
        label: 'Atendimento via assistente digital',
        payload: { kpiId: '6104', categoryGroup: categoryGroups.toolsMinus },
      },
      {
        key: 'monitorAutodiagnosticoAppSebrae',
        parentKey: 'monitorAppSebraeDropdown',
        title: 'App SEBRAE',
        label: 'Autodiagnóstico',
        payload: { kpiId: '6105', categoryGroup: categoryGroups.toolsMinus },
      },
      {
        key: 'monitorConteudoAppSebrae',
        parentKey: 'monitorAppSebraeDropdown',
        title: 'App SEBRAE',
        label: 'Conteúdo',
        payload: { kpiId: '6106', categoryGroup: categoryGroups.toolsMinus },
      },
      // Dados até 2022, motivo por estar oculto
      // {
      //   key: 'monitorCriacaoDeAnunciosEmMarketplaceAppSebrae',
      //   title: 'App SEBRAE',
      //   label: 'Criação de anúncios em marketplaces',
      //   payload: { kpiId: '6107' },
      // },
      {
        key: 'monitorFluxoDeCaixaOnlineAppSebrae',
        parentKey: 'monitorAppSebraeDropdown',
        title: 'App SEBRAE',
        label: 'Fluxo de caixa online',
        payload: { kpiId: '6108', categoryGroup: categoryGroups.toolsMinus },
      },
      {
        key: 'monitorImpressaoDeBoletoDasAppSebrae',
        parentKey: 'monitorAppSebraeDropdown',
        title: 'App SEBRAE',
        label: 'Impressão de boleto DAS',
        payload: { kpiId: '6109', categoryGroup: categoryGroups.toolsMinus },
      },
      {
        key: 'monitorRecomendacaoDeServicosDeMercadoAppSebrae',
        parentKey: 'monitorAppSebraeDropdown',
        title: 'App SEBRAE',
        label: 'Recomendação de serviços de mercado',
        payload: { kpiId: '6110', categoryGroup: categoryGroups.toolsMinus },
      },
      {
        key: 'monitorRecomendacaoDeProdutosEServicosAppSebrae',
        parentKey: 'monitorAppSebraeDropdown',
        title: 'App SEBRAE',
        label: 'Recomendação de produtos e serviços',
        payload: { kpiId: '6111', categoryGroup: categoryGroups.toolsMinus },
      },
      {
        key: 'monitorRecomendacaoDeOportunidadesDeNegocioAppSebrae',
        parentKey: 'monitorAppSebraeDropdown',
        title: 'App SEBRAE',
        label: 'Recomendação de oportunidades de negócio',
        payload: { kpiId: '6112', categoryGroup: categoryGroups.toolsMinus },
      },
      {
        key: 'monitorAtendimentoAtivoAppSebrae',
        parentKey: 'monitorAppSebraeDropdown',
        title: 'App SEBRAE',
        label: 'Atendimento ativo',
        payload: { kpiId: '6103', categoryGroup: categoryGroups.toolsMinus },
      },
    ],
  },
  {
    sectorKey: 'monitorEmissorNfe',
    entries: [
      {
        key: 'monitorAtendimentosEmissorNfe',
        parentKey: 'monitorEmissorNfeDropdown',
        title: 'Emissor NF-e',
        label: 'Atendimentos Emissor NF-e',
        payload: { kpiId: '3401', categoryGroup: categoryGroups.toolsPlus },
      },
      /* Qualquer categoria pode informar pelo payload suas unidades, 
      isso fará um override na variável, pois por padrão essas unidades são herdadas
      */
      {
        key: 'monitorNotasEmitidasNFe',
        parentKey: 'monitorEmissorNfeDropdown',
        title: 'Emissor NF-e',
        label: 'Notas emitidas',
        payload: {
          categoryGroup: categoryGroups.toolsPlus,
          kpiId: '3501',
          unitSingular: 'nota emitida',
          unitPlural: 'notas emitidas',
        },
      },
      {
        key: 'monitorValorEmitidoNfe',
        parentKey: 'monitorEmissorNfeDropdown',
        title: 'Emissor NF-e',
        label: 'Valor emitido',
        payload: {
          categoryGroup: categoryGroups.toolsPlus,
          kpiId: '3601',
          unitSingular: 'real',
          unitPlural: 'reais',
          unitFormat: 'money',
          unitFormatSuffixMoney: '',
        },
      },
      {
        key: 'distinctLegalEntitiesEmissorNfeDropdown',
        parentKey: 'monitorEmissorNfeDropdown',
        title: 'Emissor NF-e',
        label: 'Empresas distintas atendidas pelo Emissor NF-e',
        defaultChildKey: 'distinctLegalEntitiesEmissorNfe',
        payload: { categoryGroup: categoryGroups.others },
      },
      {
        key: 'distinctLegalEntitiesEmissorNfe',
        title: 'Geral',
        parentKey: 'distinctLegalEntitiesEmissorNfeDropdown',
        payload: { categoryGroup: categoryGroups.aggregatedMinus },
      },
      {
        key: 'distinctLegalEntitiesEmissorNfeSegment',
        title: 'Por segmentos',
        parentKey: 'distinctLegalEntitiesEmissorNfeDropdown',
        payload: { categoryGroup: categoryGroups.others },
      },
      {
        key: 'distinctLegalEntitiesEmissorNfeCnaeDivision',
        title: 'Por divisão CNAE',
        parentKey: 'distinctLegalEntitiesEmissorNfeDropdown',
        payload: { categoryGroup: categoryGroups.others },
      },
      {
        key: 'distinctLegalEntitiesEmissorNfeSize',
        title: 'Por portes',
        parentKey: 'distinctLegalEntitiesEmissorNfeDropdown',
        payload: { categoryGroup: categoryGroups.others },
      },
      {
        key: 'distinctLegalEntitiesEmissorNfeMaturity',
        title: 'Por maturidade',
        parentKey: 'distinctLegalEntitiesEmissorNfeDropdown',
        payload: { categoryGroup: categoryGroups.others },
      },
      // Teremos dados apenas em 2026
      // {
      //   key: 'monitorNumeroDeEmitentesNFe',
      //   title: 'Emissor NF-e',
      //   label: 'Número de emitentes',
      //   payload: { kpiId: '3701' },
      // },
    ],
  },
  {
    sectorKey: 'monitorConecta',
    entries: [
      {
        key: 'monitorAtendimentosConecta',
        parentKey: 'monitorConectaDropdown',
        title: 'Conecta',
        label: 'Atendimentos Conecta',
        payload: { kpiId: '5301', categoryGroup: categoryGroups.toolsPlus },
      },
      {
        key: 'monitorAtendimentosPorParceiroConecta',
        parentKey: 'monitorConectaDropdown',
        title: 'Conecta',
        label: 'Atendimentos por parceiro',
        payload: {
          kpiId: '5401',
          attendancesPerProduct: true,
          categoryGroup: categoryGroups.toolsMinus,
        },
      },
    ],
  },
  {
    sectorKey: 'monitorProductFamilies',
    entries: [
      {
        key: 'sebraeIngresse',
        title: 'SEBRAE Ingresse',
        label: 'Atendimentos SEBRAE Ingresse',
        payload: {
          kpiId: '1701',
          attendancesPerProduct: true,
          categoryGroup: categoryGroups.aggregatedManagement,
          categoryKeys: [gestaoEmpresarialSolutionPerformanceCategoryKey],
          productCodes: [...monitorProductFamilyCodes.sebraeIngresse],
        },
      },
      {
        key: 'familiaEmpretec',
        title: 'Família Empretec',
        label: 'Atendimentos Família Empretec',
        payload: {
          kpiId: '1701',
          attendancesPerProduct: true,
          categoryGroup: categoryGroups.aggregatedManagement,
          categoryKeys: [gestaoEmpresarialSolutionPerformanceCategoryKey],
          productCodes: [...monitorProductFamilyCodes.familiaEmpretec],
        },
      },
    ],
  },
  {
    sectorKey: 'monitorPortfolio',
    entries: [
      ...monitorPortfolioEntries.filter(entry => !!entry.parentKey),
      {
        key: 'generalPortfolioOutOfNetwork',
        title: 'Índice de Desempenho de Soluções (IDS)',
        parentKey: 'monitorPortfolioOutOfNetwork',
        payload: { categoryGroup: categoryGroups.others },
      },
    ],
  },
  {
    sectorKey: 'agentsSebraeInYourCompany',
    entries: [
      {
        key: 'appSebraeAttendance',
        title: 'Geral',
        payload: { categoryGroup: categoryGroups.aggregatedMinus },
      },

      {
        key: 'appSebraeAttendanceSegment',
        title: 'Por segmento',
        payload: { categoryGroup: categoryGroups.others },
      },
      {
        key: 'appSebraeAttendanceCnaeDivision',
        title: 'Por divisão CNAE',
        payload: { categoryGroup: categoryGroups.others },
      },
      {
        key: 'appSebraeAttendanceSize',
        title: 'Por portes',
        payload: { categoryGroup: categoryGroups.others },
      },
      {
        key: 'appSebraeAttendanceMaturity',
        title: 'Por maturidade',
        payload: { categoryGroup: categoryGroups.others },
      },
    ],
  },
  {
    sectorKey: 'legalEntitiesDropdown',
    entries: [
      {
        key: 'legalEntities',
        title: 'Todas as empresas',
      },
      {
        key: 'legalEntityActiveAndInaptSegment',
        title: 'Empresas por segmentos',
      },
      {
        key: 'legalEntityActiveAndInaptSize',
        title: 'Empresas por portes',
      },
      {
        key: 'legalEntityActiveAndInaptMaturity',
        title: 'Empresas por maturidades',
      },
      {
        key: 'legalEntityActiveAndInaptCnaeDivision',
        title: 'Empresas por divisão CNAE',
      },
    ],
  },
]

export const standaloneCategoryData: RawStandaloneCategory[] = [
  {
    key: 'all',
    title: 'Tudo',
    sectorKey: 'general',
    payload: { categoryGroup: categoryGroups.others },
  },
  {
    key: 'monitorServiceOverviewDropdown',
    title: 'Visão geral do atendimento',
    sectorKey: 'portfolioSolutions',
    defaultChildKey: 'monitorServiceOverview',
  },
  {
    key: 'monitorOnlineCoursesDropdown',
    title: 'Cursos online',
    sectorKey: 'portfolioSolutions',
    defaultChildKey: 'monitorAttendanceEad',
  },
  {
    key: 'monitorEmissorNfeDropdown',
    title: 'Emissor NF-e',
    sectorKey: 'portfolioSolutions',
    defaultChildKey: 'monitorAtendimentosEmissorNfe',
  },
  {
    key: 'monitorAppSebraeDropdown',
    title: 'App SEBRAE',
    sectorKey: 'portfolioSolutions',
    defaultChildKey: 'monitorAtendimentosAppSebrae',
  },
  {
    key: 'monitorConectaDropdown',
    title: 'Conecta',
    sectorKey: 'portfolioSolutions',
    defaultChildKey: 'monitorAtendimentosConecta',
  },
  {
    key: 'monitorPortfolioDropdown',
    title: 'Portfólios',
    sectorKey: 'portfolioSolutions',
  },
  // {
  //   key: 'generalPortfolio',
  //   title: 'Portfólios',
  //   sectorKey: 'general',
  // },
  // {
  //   key: 'generalPortfolioOutOfNetworkAttendancesPerProduct',
  //   title: 'Atendimentos por produto',
  //   sectorKey: 'monitorPortfolioOutOfNetwork',
  //   payload: {
  //     categoryGroup: categoryGroups.others,
  //     kpiId: '1701',
  //     attendancesPerProduct: true,
  //   },
  // },
  ...monitorPortfolioEntries
    .filter(entry => !entry.parentKey)
    .map(entry => ({
      parentKey: 'monitorPortfolioDropdown',
      sectorKey: 'monitorPortfolioDropdown',
      ...entry,
    })),
  {
    key: 'monitorPortfolioOutOfNetwork',
    title: 'Fora dos portfólios da rede',
    parentKey: 'monitorPortfolioDropdown',
    sectorKey: 'monitorPortfolioDropdown',
  },
]
